import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { colors } from './theme';
import LoadingSpinner from './components/LoadingSpinner';
import NoAccessNotice from './components/NoAccessNotice';

const API_BASE = process.env.REACT_APP_API_URL;

// ── Tiny shared helpers ───────────────────────────────────────────────────────

function Label({ children, style }) {
  return <div style={{ fontSize: '0.7rem', color: colors.textDim, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', ...style }}>{children}</div>;
}
function Val({ children }) {
  return <div style={{ fontSize: '0.88rem', color: colors.textBody, marginTop: '0.2rem' }}>{children ?? '—'}</div>;
}
function Pair({ label, value }) {
  return <div><Label>{label}</Label><Val>{value}</Val></div>;
}
function Badge({ label, color }) {
  return <span style={{ fontSize: '0.68rem', fontWeight: 700, background: `${color}20`, color, borderRadius: 4, padding: '0.1rem 0.45rem', whiteSpace: 'nowrap' }}>{label}</span>;
}
function SectionTitle({ children }) {
  return <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textDim, marginBottom: '0.65rem' }}>{children}</div>;
}
function Panel({ children, style }) {
  return <div style={{ background: colors.surfaceMuted, borderRadius: 8, padding: '0.85rem', ...style }}>{children}</div>;
}

const STATUS_COLOR = {
  draft: colors.textDim, in_progress: colors.primary, testing: colors.statusYellow,
  completed: colors.success, cancelled: colors.danger,
};
const STATUS_LABEL = {
  draft: 'Draft', in_progress: 'In Progress', testing: 'Testing',
  completed: 'Completed', cancelled: 'Cancelled',
};

// ── Main page ─────────────────────────────────────────────────────────────────

export default function RecommissionPage() {
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const role = user?.publicMetadata?.role || 'guest';
  const isAdmin = role === 'admin';
  const isService = String(role).toLowerCase().startsWith('service');

  const [tab, setTab] = useState('decommission');
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  const lastApiError = React.useRef('');
  const apiCall = useCallback(async (path, method = 'GET', body = null) => {
    try {
      const token = await getToken();
      const opts = { method, headers: { Authorization: `Bearer ${token}` } };
      if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
      const res = await fetch(`${API_BASE}${path}`, opts);
      const data = await res.json();
      if (!data.ok) {
        const msg = data.message || data.error || 'Error';
        lastApiError.current = msg;
        setError(msg);
        return null;
      }
      lastApiError.current = '';
      setError('');
      return data;
    } catch {
      lastApiError.current = 'Network error.';
      setError('Network error.');
      return null;
    }
  }, [getToken]);

  useEffect(() => {
    if (!isAdmin && !isService) return;
    apiCall('/recommission/stats').then(d => d && setStats(d.stats));
  }, [apiCall, isAdmin, isService]);

  if (!isSignedIn || (!isAdmin && !isService)) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><NoAccessNotice pageName="Recommissioning" /></div>;
  }

  const TABS = [
    { key: 'decommission', label: 'Decommission Monitor' },
    { key: 'parts', label: 'Part Inventory' },
    { key: 'builds', label: 'Recommission Builds' },
    { key: 'completed', label: 'Completed' },
  ];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '1.25rem 1.5rem 0', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div className="ni-brand" style={{ flex: 1, minWidth: 0 }}>
          <Link to="/" title="Back to main page" aria-label="Back to main page" style={{ display: 'inline-flex', lineHeight: 0 }}>
            <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
          </Link>
          <div className="ni-brand-copy">
            <span className="ni-eyebrow">Recommission Program</span>
            <span className="ni-brand-subtitle">Decommission monitors · Harvest ELU &amp; DIU · Build new units</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Link to="/rmas" className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem', textDecoration: 'none' }}>RMA Board</Link>
          <Link to="/service/refurbished" className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem', textDecoration: 'none' }}>Refurbished</Link>
        </div>
      </div>

      {/* Stats bar */}
      {stats && (
        <div style={{ display: 'flex', gap: '1.5rem', padding: '0.85rem 1.5rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Decommissioned Monitors', value: stats.decommissioned, color: colors.textMuted },
            { label: 'ELU / DIU Available', value: stats.partsAvailable, color: colors.success },
            { label: 'Parts in Use', value: stats.partsInUse, color: colors.primary },
            { label: 'Active Builds', value: stats.buildsActive, color: colors.statusYellow },
            { label: 'Completed Builds', value: stats.buildsCompleted, color: colors.success },
          ].map(s => (
            <div key={s.label} style={{ background: colors.surfaceMuted, borderRadius: 8, padding: '0.6rem 1rem', minWidth: 120 }}>
              <Label>{s.label}</Label>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: s.color, marginTop: '0.15rem' }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${colors.panelBorder}`, padding: '0 1.5rem' }}>
        {TABS.map(t => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '0.6rem 1rem', fontSize: '0.8rem', fontWeight: 600,
            color: tab === t.key ? colors.primary : colors.textMuted,
            borderBottom: tab === t.key ? `2px solid ${colors.primary}` : '2px solid transparent',
          }}>{t.label}</button>
        ))}
      </div>

      {error && <div style={{ margin: '0.75rem 1.5rem 0', background: `${colors.danger}15`, border: `1px solid ${colors.danger}40`, borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.82rem', color: colors.danger }}>{error}</div>}

      <div style={{ flex: 1, padding: '1.25rem 1.5rem' }}>
        {tab === 'decommission' && <DecommissionTab apiCall={apiCall} onDecommissioned={() => { apiCall('/recommission/stats').then(d => d && setStats(d.stats)); }} />}
        {tab === 'parts' && <PartsTab apiCall={apiCall} onBuildStarted={() => setTab('builds')} />}
        {tab === 'builds' && <BuildsTab apiCall={apiCall} lastApiError={lastApiError} onRefreshStats={() => apiCall('/recommission/stats').then(d => d && setStats(d.stats))} />}
        {tab === 'completed' && <CompletedTab apiCall={apiCall} />}
      </div>
    </div>
  );
}

// ── Decommission Tab ──────────────────────────────────────────────────────────

function DecommissionTab({ apiCall, onDecommissioned }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const search = async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    const d = await apiCall(`/recommission/search-monitor?q=${encodeURIComponent(q)}`);
    setSearching(false);
    if (d) setResults(d.results);
  };

  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDecommission = async () => {
    if (!selected) return;
    setSubmitting(true);
    const d = await apiCall('/recommission/decommission', 'POST', { productionDataId: selected._id, notes });
    setSubmitting(false);
    if (d) {
      setDone(d);
      setSelected(null);
      setQuery('');
      setResults([]);
      setNotes('');
      onDecommissioned();
    }
  };

  return (
    <div style={{ maxWidth: 680, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <Panel>
        <SectionTitle>Find Monitor to Decommission</SectionTitle>
        <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: '0 0 0.85rem' }}>
          Search for a MONITOR by service tag or serial number. The device will be split into an ELU and DIU that are added to the part inventory. The original service tag will be decommissioned.
        </p>
        <input
          value={query} onChange={e => setQuery(e.target.value)}
          className="ni-input" placeholder="Service tag or serial number..."
          style={{ marginBottom: '0.65rem' }}
        />
        {searching && <div style={{ color: colors.textDim, fontSize: '0.8rem' }}>Searching…</div>}
        {results.length > 0 && !selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {results.map(r => (
              <div key={r._id} onClick={() => { setSelected(r); setResults([]); setQuery(r.serviceTagNumber || r.serialNumber); }}
                style={{ background: colors.surface, border: `1px solid ${colors.panelBorder}`, borderRadius: 8, padding: '0.65rem 0.85rem', cursor: 'pointer', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', color: colors.textStrong }}>{r.serviceTagNumber || '—'}</div>
                  <div style={{ fontSize: '0.75rem', color: colors.textDim }}>{r.serialNumber}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.82rem', color: colors.textMuted }}>{r.model || r.productNumber || '—'}</div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {r.snElu && <Badge label={`ELU: ${r.snElu}`} color={colors.primary} />}
                  {r.snDisplay && <Badge label={`DIU: ${r.snDisplay}`} color={colors.statusYellow} />}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {selected && (
        <Panel style={{ border: `1px solid ${colors.danger}40` }}>
          <SectionTitle style={{ color: colors.danger }}>Confirm Decommission</SectionTitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', marginBottom: '1rem' }}>
            <Pair label="Service Tag" value={<span style={{ fontWeight: 700, color: colors.textStrong }}>{selected.serviceTagNumber || '—'}</span>} />
            <Pair label="Serial Number" value={selected.serialNumber} />
            <Pair label="Model" value={selected.model || '—'} />
            <Pair label="Product No." value={selected.productNumber || '—'} />
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem' }}>
            <div style={{ background: `${colors.primary}10`, border: `1px solid ${colors.primary}25`, borderRadius: 8, padding: '0.65rem 0.85rem', flex: 1 }}>
              <Label style={{ color: colors.primary }}>ELU Will Be Harvested</Label>
              <Val>{selected.snElu || '—'}</Val>
              {selected.mainBoard && <div style={{ fontSize: '0.75rem', color: colors.textDim, marginTop: '0.2rem' }}>Main Board: {selected.mainBoard}</div>}
            </div>
            <div style={{ background: `${colors.statusYellow}10`, border: `1px solid ${colors.statusYellow}25`, borderRadius: 8, padding: '0.65rem 0.85rem', flex: 1 }}>
              <Label style={{ color: colors.statusYellow }}>DIU Will Be Harvested</Label>
              <Val>{selected.snDisplay || selected.snDisplayCabinet || '—'}</Val>
              {selected.snColor && <div style={{ fontSize: '0.75rem', color: colors.textDim, marginTop: '0.2rem' }}>Color: {selected.snColor}</div>}
            </div>
          </div>
          <Label>Notes (optional)</Label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} className="ni-input" rows={2} placeholder="Reason for decommissioning..." style={{ resize: 'vertical', marginTop: '0.3rem', marginBottom: '0.75rem' }} />
          <div style={{ display: 'flex', gap: '0.65rem' }}>
            <button onClick={handleDecommission} disabled={submitting} className="ni-button ni-button--primary">
              {submitting ? 'Processing…' : 'Decommission — Split into ELU + DIU'}
            </button>
            <button onClick={() => { setSelected(null); setQuery(''); }} className="ni-button ni-button--ghost">Cancel</button>
          </div>
        </Panel>
      )}

      {done && (
        <Panel style={{ border: `1px solid ${colors.success}40` }}>
          <div style={{ fontWeight: 700, color: colors.success, marginBottom: '0.65rem' }}>✓ Decommissioned successfully</div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ background: `${colors.primary}10`, borderRadius: 8, padding: '0.65rem 0.85rem', flex: 1 }}>
              <Label style={{ color: colors.primary }}>ELU Added to Inventory</Label>
              <Val>{done.eluPart?.partId}</Val>
              <div style={{ fontSize: '0.75rem', color: colors.textDim }}>{done.eluPart?.serialNumber}</div>
            </div>
            <div style={{ background: `${colors.statusYellow}10`, borderRadius: 8, padding: '0.65rem 0.85rem', flex: 1 }}>
              <Label style={{ color: colors.statusYellow }}>DIU Added to Inventory</Label>
              <Val>{done.diuPart?.partId}</Val>
              <div style={{ fontSize: '0.75rem', color: colors.textDim }}>{done.diuPart?.serialNumber}</div>
            </div>
          </div>
          <button onClick={() => setDone(null)} className="ni-button ni-button--ghost" style={{ marginTop: '0.75rem', fontSize: '0.8rem' }}>Dismiss</button>
        </Panel>
      )}
    </div>
  );
}

// ── Parts Inventory Tab ───────────────────────────────────────────────────────

function PartsTab({ apiCall, onBuildStarted }) {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('available');
  const [showBuildModal, setShowBuildModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (typeFilter) params.set('type', typeFilter);
    if (statusFilter) params.set('status', statusFilter);
    const d = await apiCall(`/recommission/parts?${params}`);
    setLoading(false);
    if (d) setParts(d.parts);
  }, [apiCall, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const elus = parts.filter(p => p.type === 'ELU');
  const dius = parts.filter(p => p.type === 'DIU');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', gap: '0.65rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="ni-input" style={{ width: 'auto', fontSize: '0.82rem' }}>
          <option value="">All types</option>
          <option value="ELU">ELU only</option>
          <option value="DIU">DIU only</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="ni-input" style={{ width: 'auto', fontSize: '0.82rem' }}>
          <option value="">All statuses</option>
          <option value="available">Available</option>
          <option value="in_use">In Use</option>
          <option value="scrapped">Scrapped</option>
        </select>
        {elus.filter(p => p.status === 'available').length > 0 && dius.filter(p => p.status === 'available').length > 0 && (
          <button onClick={() => setShowBuildModal(true)} className="ni-button ni-button--primary" style={{ fontSize: '0.82rem', marginLeft: 'auto' }}>
            + Start Recommission Build
          </button>
        )}
      </div>

      {loading ? <LoadingSpinner size={40} /> : parts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: colors.textDim }}>No parts found.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.65rem' }}>
          {parts.map(p => <PartCard key={p._id} part={p} />)}
        </div>
      )}

      {showBuildModal && (
        <StartBuildModal
          apiCall={apiCall}
          availableElus={elus.filter(p => p.status === 'available')}
          availableDius={dius.filter(p => p.status === 'available')}
          onClose={() => setShowBuildModal(false)}
          onCreated={() => { setShowBuildModal(false); onBuildStarted(); }}
        />
      )}
    </div>
  );
}

function PartCard({ part }) {
  const isElu = part.type === 'ELU';
  const accent = isElu ? colors.primary : colors.statusYellow;
  const statusColor = part.status === 'available' ? colors.success : part.status === 'in_use' ? colors.primary : colors.danger;

  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.panelBorder}`, borderRadius: 8, padding: '0.75rem', borderTop: `2px solid ${accent}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <div>
          <Badge label={part.type} color={accent} />
          <div style={{ fontSize: '0.78rem', color: colors.textDim, marginTop: '0.2rem' }}>{part.partId}</div>
        </div>
        <Badge label={part.status.replace('_', ' ').toUpperCase()} color={statusColor} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', fontSize: '0.82rem' }}>
        {part.serialNumber && <Pair label="Serial" value={part.serialNumber} />}
        <Pair label="From Monitor" value={part.originServiceTag || '—'} />
        <Pair label="Product No." value={part.originProductNumber || '—'} />
        {part.condition !== 'good' && <Pair label="Condition" value={<Badge label={part.condition} color={colors.statusYellow} />} />}
      </div>
      {part.status === 'in_use' && part.usedInBuildId && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: colors.primary }}>Build: {part.usedInBuildId}</div>
      )}
      {part.decommissionedAt && (
        <div style={{ marginTop: '0.35rem', fontSize: '0.72rem', color: colors.textDim }}>
          Harvested {new Date(part.decommissionedAt).toLocaleDateString('en-CA')} by {part.decommissionedBy}
        </div>
      )}
    </div>
  );
}

// ── Start Build Modal ─────────────────────────────────────────────────────────

function StartBuildModal({ apiCall, availableElus, availableDius, onClose, onCreated }) {
  const [eluPartId, setEluPartId] = useState(availableElus[0]?.partId || '');
  const [diuPartId, setDiuPartId] = useState(availableDius[0]?.partId || '');
  const [newServiceTag, setNewServiceTag] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const handleCreate = async () => {
    if (!newServiceTag.trim()) { setErr('Service tag is required.'); return; }
    setSubmitting(true);
    const d = await apiCall('/recommission/builds', 'POST', { eluPartId, diuPartId, newServiceTag: newServiceTag.trim(), notes });
    setSubmitting(false);
    if (d) onCreated(d.build);
    else setErr('Failed to create build.');
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: colors.surface, border: `1px solid ${colors.panelBorder}`, borderRadius: 14, width: '100%', maxWidth: 520, padding: '1.5rem', boxShadow: colors.shadow }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: colors.textStrong }}>Start Recommission Build</div>
          <button onClick={onClose} className="ni-button ni-button--ghost" style={{ padding: '0.2rem 0.55rem' }}>✕</button>
        </div>

        {err && <div style={{ background: `${colors.danger}15`, border: `1px solid ${colors.danger}40`, borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.82rem', color: colors.danger, marginBottom: '0.75rem' }}>{err}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <div>
            <Label>Select ELU ({availableElus.length} available)</Label>
            <select value={eluPartId} onChange={e => setEluPartId(e.target.value)} className="ni-input" style={{ marginTop: '0.3rem' }}>
              {availableElus.map(p => (
                <option key={p.partId} value={p.partId}>{p.partId} — {p.serialNumber || 'no serial'} (from {p.originServiceTag || '—'})</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Select DIU ({availableDius.length} available)</Label>
            <select value={diuPartId} onChange={e => setDiuPartId(e.target.value)} className="ni-input" style={{ marginTop: '0.3rem' }}>
              {availableDius.map(p => (
                <option key={p.partId} value={p.partId}>{p.partId} — {p.serialNumber || 'no serial'} (from {p.originServiceTag || '—'})</option>
              ))}
            </select>
          </div>
          <div>
            <Label>New Service Tag <span style={{ color: colors.danger }}>*</span></Label>
            <input value={newServiceTag} onChange={e => setNewServiceTag(e.target.value)} className="ni-input" placeholder="e.g. NI-2026-0042" style={{ marginTop: '0.3rem' }} />
            <div style={{ fontSize: '0.72rem', color: colors.textDim, marginTop: '0.2rem' }}>This becomes the MONITOR's new identity. Must be unique.</div>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="ni-input" rows={2} style={{ resize: 'vertical', marginTop: '0.3rem' }} />
          </div>
          <div style={{ display: 'flex', gap: '0.65rem', paddingTop: '0.25rem' }}>
            <button onClick={handleCreate} disabled={submitting} className="ni-button ni-button--primary">{submitting ? 'Creating…' : 'Create Build'}</button>
            <button onClick={onClose} className="ni-button ni-button--ghost">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Builds Tab ────────────────────────────────────────────────────────────────

function BuildsTab({ apiCall, lastApiError, onRefreshStats }) {
  const [builds, setBuilds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBuild, setActiveBuild] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const d = await apiCall('/recommission/builds?status=draft,in_progress,testing');
    setLoading(false);
    if (d) setBuilds(d.builds);
  }, [apiCall]);

  useEffect(() => { load(); }, [load]);

  const handleUpdated = (build) => {
    setBuilds(prev => prev.map(b => b._id === build._id ? build : b));
    setActiveBuild(build);
    if (build.status === 'completed' || build.status === 'cancelled') {
      setActiveBuild(null);
      load();
      onRefreshStats();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {loading ? <LoadingSpinner size={40} /> : builds.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: colors.textDim }}>No active builds. Start one from the Part Inventory tab.</div>
      ) : (
        builds.map(b => (
          <BuildCard key={b._id} build={b} onClick={() => setActiveBuild(b)} />
        ))
      )}
      {activeBuild && (
        <BuildDetailModal apiCall={apiCall} lastApiError={lastApiError} buildId={activeBuild._id} onClose={() => setActiveBuild(null)} onUpdated={handleUpdated} />
      )}
    </div>
  );
}

function BuildCard({ build, onClick }) {
  const statusColor = STATUS_COLOR[build.status] || colors.textDim;
  return (
    <div onClick={onClick} style={{ background: colors.surface, border: `1px solid ${colors.panelBorder}`, borderRadius: 8, padding: '0.85rem 1rem', cursor: 'pointer', display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
      <div style={{ minWidth: 140 }}>
        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: colors.textStrong }}>{build.buildId}</div>
        <Badge label={STATUS_LABEL[build.status] || build.status} color={statusColor} />
      </div>
      <Pair label="New Service Tag" value={<span style={{ fontWeight: 700, color: colors.primary }}>{build.newServiceTag || '—'}</span>} />
      {build.newProductNumber && <Pair label="Product No." value={build.newProductNumber} />}
      <div style={{ flex: 1 }}>
        <Label>Source</Label>
        <Val>{build.sourceSummary || '—'}</Val>
      </div>
      <div style={{ fontSize: '0.72rem', color: colors.textDim }}>{new Date(build.createdAt).toLocaleDateString('en-CA')}</div>
    </div>
  );
}

// ── Build Detail Modal ────────────────────────────────────────────────────────

function BuildDetailModal({ apiCall, lastApiError, buildId, onClose, onUpdated }) {
  const [build, setBuild] = useState(null);
  const [eluPart, setEluPart] = useState(null);
  const [diuPart, setDiuPart] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({});
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    apiCall(`/recommission/builds/${buildId}`).then(d => {
      if (!d) return;
      setBuild(d.build);
      setEluPart(d.eluPart);
      setDiuPart(d.diuPart);
      // Pre-fill form
      const b = d.build;
      setForm({
        newProductNumber: b.newProductNumber, newModel: b.newModel, newWorkorder: b.newWorkorder,
        newPoNumber: b.newPoNumber, newSaleOrder: b.newSaleOrder, newSerialNumber: b.newSerialNumber,
        newServiceTag: b.newServiceTag,
        drawingDIU: b.drawingDIU, workDIU: b.workDIU, cusPnDIU: b.cusPnDIU,
        snDisplayCabinet: b.snDisplayCabinet, snDisplay: b.snDisplay, snColor: b.snColor,
        drawingELU: b.drawingELU, workELU: b.workELU, cusPnELU: b.cusPnELU, snElu: b.snElu,
        mainBoard: b.mainBoard, multiPowerBoard: b.multiPowerBoard, topPcbBoard: b.topPcbBoard,
        serialSsd: b.serialSsd, ssdType: b.ssdType, serialRam: b.serialRam, ramType: b.ramType,
        assembledBy: b.assembledBy, testedBy: b.testedBy, testResult: b.testResult, testNotes: b.testNotes,
        notes: b.notes, internalNotes: b.internalNotes,
      });
    });
  }, [buildId]); // eslint-disable-line react-hooks/exhaustive-deps

  const f = (key) => ({ value: form[key] || '', onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) });

  const save = async (extraFields = {}) => {
    setSaving(true); setErr('');
    const d = await apiCall(`/recommission/builds/${build._id}`, 'PUT', { ...form, ...extraFields });
    setSaving(false);
    if (d) { setBuild(d.build); onUpdated(d.build); return d.build; }
    else setErr('Save failed.');
    return null;
  };

  const complete = async () => {
    setSaving(true); setErr('');
    const putOk = await apiCall(`/recommission/builds/${build._id}`, 'PUT', form);
    if (!putOk) {
      setSaving(false);
      setErr(`Save failed: ${lastApiError?.current || 'unknown error'}`);
      return;
    }
    const d = await apiCall(`/recommission/builds/${build._id}/complete`, 'POST', {});
    setSaving(false);
    if (d) onUpdated(d.build);
    else setErr(`Completion failed: ${lastApiError?.current || 'unknown error'}`);
  };

  const cancel = async () => {
    setSaving(true);
    const d = await apiCall(`/recommission/builds/${build._id}/cancel`, 'POST', { reason: cancelReason });
    setSaving(false);
    if (d) onUpdated(d.build);
  };

  if (!build) return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <LoadingSpinner size={56} />
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '1.5rem 1rem' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: colors.surface, border: `1px solid ${colors.panelBorder}`, borderRadius: 14, width: '100%', maxWidth: 760, boxShadow: colors.shadow }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: `1px solid ${colors.panelBorder}` }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: colors.textStrong }}>{build.buildId}</div>
            <div style={{ display: 'flex', gap: '0.65rem', marginTop: '0.25rem', alignItems: 'center' }}>
              <Badge label={STATUS_LABEL[build.status] || build.status} color={STATUS_COLOR[build.status]} />
              {build.newServiceTag && <span style={{ fontSize: '0.82rem', fontWeight: 700, color: colors.primary }}>{build.newServiceTag}</span>}
            </div>
          </div>
          <button onClick={onClose} className="ni-button ni-button--ghost" style={{ padding: '0.25rem 0.6rem' }}>✕</button>
        </div>

        <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {err && <div style={{ background: `${colors.danger}15`, border: `1px solid ${colors.danger}40`, borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.82rem', color: colors.danger }}>{err}</div>}

          {/* Parts summary */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {eluPart && (
              <div style={{ flex: 1, minWidth: 200, background: `${colors.primary}10`, border: `1px solid ${colors.primary}25`, borderRadius: 8, padding: '0.65rem 0.85rem' }}>
                <Label style={{ color: colors.primary }}>ELU — {eluPart.partId}</Label>
                <Val>{eluPart.serialNumber || '—'}</Val>
                <div style={{ fontSize: '0.75rem', color: colors.textDim }}>From: {eluPart.originServiceTag || '—'}</div>
              </div>
            )}
            {diuPart && (
              <div style={{ flex: 1, minWidth: 200, background: `${colors.statusYellow}10`, border: `1px solid ${colors.statusYellow}25`, borderRadius: 8, padding: '0.65rem 0.85rem' }}>
                <Label style={{ color: colors.statusYellow }}>DIU — {diuPart.partId}</Label>
                <Val>{diuPart.serialNumber || '—'}</Val>
                <div style={{ fontSize: '0.75rem', color: colors.textDim }}>From: {diuPart.originServiceTag || '—'}</div>
              </div>
            )}
          </div>

          {/* New product identity */}
          <div>
            <SectionTitle>New Monitor Identity</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.65rem' }}>
              {[['newServiceTag','Service Tag *'],['newSerialNumber','Serial Number (leave blank to auto-generate)'],['newProductNumber','Product Number'],['newModel','Model'],['newWorkorder','Work Order'],['newPoNumber','PO Number'],['newSaleOrder','Sale Order']].map(([k, lbl]) => (
                <div key={k}>
                  <Label>{lbl}</Label>
                  <input {...f(k)} className="ni-input" style={{ marginTop: '0.3rem' }} />
                </div>
              ))}
            </div>
          </div>

          {/* DIU data */}
          <div>
            <SectionTitle>DIU Component Data</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.65rem' }}>
              {[['drawingDIU','Drawing DIU'],['workDIU','Work DIU'],['cusPnDIU','Cust. PN DIU'],['snDisplayCabinet','SN Cabinet'],['snDisplay','SN Display'],['snColor','SN Color']].map(([k, lbl]) => (
                <div key={k}><Label>{lbl}</Label><input {...f(k)} className="ni-input" style={{ marginTop: '0.3rem' }} /></div>
              ))}
            </div>
          </div>

          {/* ELU data */}
          <div>
            <SectionTitle>ELU Component Data</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.65rem' }}>
              {[['drawingELU','Drawing ELU'],['workELU','Work ELU'],['cusPnELU','Cust. PN ELU'],['snElu','SN ELU'],['mainBoard','Main Board'],['multiPowerBoard','Multi Power Board'],['topPcbBoard','Top PCB Board'],['serialSsd','Serial SSD'],['ssdType','SSD Type'],['serialRam','Serial RAM'],['ramType','RAM Type']].map(([k, lbl]) => (
                <div key={k}><Label>{lbl}</Label><input {...f(k)} className="ni-input" style={{ marginTop: '0.3rem' }} /></div>
              ))}
            </div>
          </div>

          {/* Assembly & test */}
          <div>
            <SectionTitle>Assembly &amp; Testing</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.65rem' }}>
              <div><Label>Assembled By</Label><input {...f('assembledBy')} className="ni-input" style={{ marginTop: '0.3rem' }} /></div>
              <div><Label>Tested By</Label><input {...f('testedBy')} className="ni-input" style={{ marginTop: '0.3rem' }} /></div>
              <div>
                <Label>Test Result</Label>
                <select {...f('testResult')} className="ni-input" style={{ marginTop: '0.3rem' }}>
                  <option value="">Not tested</option>
                  <option value="pass">Pass</option>
                  <option value="fail">Fail</option>
                </select>
              </div>
              <div><Label>Test Notes</Label><input {...f('testNotes')} className="ni-input" style={{ marginTop: '0.3rem' }} /></div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
            <div><Label>Notes</Label><textarea {...f('notes')} className="ni-input" rows={2} style={{ resize: 'vertical', marginTop: '0.3rem' }} /></div>
            <div><Label>Internal Notes</Label><textarea {...f('internalNotes')} className="ni-input" rows={2} style={{ resize: 'vertical', marginTop: '0.3rem' }} /></div>
          </div>

          {/* Warranty info */}
          <div style={{ background: `${colors.success}10`, border: `1px solid ${colors.success}25`, borderRadius: 8, padding: '0.65rem 0.85rem', fontSize: '0.82rem', color: colors.success }}>
            ✓ Completing this build will generate a new ProductionData record with a <strong>3-year warranty</strong> from today.
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '1rem' }}>
            <button onClick={() => save()} disabled={saving} className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem' }}>{saving ? 'Saving…' : 'Save Progress'}</button>
            <button onClick={complete} disabled={saving || build.status === 'completed'} className="ni-button ni-button--primary">
              {saving ? 'Processing…' : '✓ Complete Build — Create New Monitor'}
            </button>
            <button onClick={() => setShowCancel(v => !v)} className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem', color: colors.danger, borderColor: `${colors.danger}60`, marginLeft: 'auto' }}>
              Cancel Build
            </button>
          </div>
          {showCancel && (
            <div style={{ background: `${colors.danger}10`, border: `1px solid ${colors.danger}30`, borderRadius: 8, padding: '0.75rem' }}>
              <Label style={{ color: colors.danger }}>Confirm Cancel — parts will be released back to inventory</Label>
              <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} className="ni-input" rows={2} placeholder="Reason for cancellation..." style={{ resize: 'vertical', marginTop: '0.35rem', marginBottom: '0.5rem' }} />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={cancel} disabled={saving} className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }}>{saving ? '…' : 'Confirm Cancel'}</button>
                <button onClick={() => setShowCancel(false)} className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem' }}>Back</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Completed Tab ─────────────────────────────────────────────────────────────

function CompletedTab({ apiCall }) {
  const [builds, setBuilds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiCall('/recommission/builds?status=completed').then(d => {
      setLoading(false);
      if (d) setBuilds(d.builds);
    });
  }, [apiCall]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
      {loading ? <LoadingSpinner size={40} /> : builds.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: colors.textDim }}>No completed builds yet.</div>
      ) : builds.map(b => (
        <div key={b._id} style={{ background: colors.surface, border: `1px solid ${colors.panelBorder}`, borderRadius: 8, padding: '0.85rem 1rem' }}>
          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: colors.textStrong }}>{b.buildId}</div>
              <Badge label="Completed" color={colors.success} />
            </div>
            <Pair label="New Service Tag" value={<span style={{ fontWeight: 700, color: colors.primary }}>{b.newServiceTag}</span>} />
            {b.newSerialNumber && <Pair label="New Serial" value={b.newSerialNumber} />}
            {b.newProductNumber && <Pair label="Product No." value={b.newProductNumber} />}
            <Pair label="Completed" value={b.completedAt ? new Date(b.completedAt).toLocaleDateString('en-CA') : '—'} />
            <Pair label="Completed By" value={b.completedBy || '—'} />
            {b.warrantyExpiresAt && (
              <Pair label="Warranty Expires" value={<span style={{ color: colors.success, fontWeight: 700 }}>{new Date(b.warrantyExpiresAt).toLocaleDateString('en-CA')}</span>} />
            )}
            <div style={{ flex: 1 }}>
              <Label>Source</Label>
              <Val>{b.sourceSummary || '—'}</Val>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
